import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { STORAGE_DEALS_SEARCH_PATH } from 'config/search';

import { HowItWorks } from 'components/brand/HowItWorks';
import { PaginationSummary } from 'components/core/Pagination';
import { SortBySelectBox } from 'components/search/SortBySelectBox';
import { H1, H2, Text, Paragraph } from 'components/core/Typography';
import { Section, Container, Group } from 'components/core/Page';
import { Button } from 'components/core/Button';
import { Link } from 'components/core/Link';
import { Pill } from 'components/core/Pill';
import { StaticMap } from 'components/core/StaticMap';
import { CallCenterCTA } from 'components/contact/CallCenterCTA';
import { SearchBox } from 'components/search/SearchBox';
import { SearchHero } from 'components/search/SearchHero';
import { SearchPagination } from 'components/search/SearchPagination';
import { FiltersButton } from 'components/search/FiltersButton';
import { FacilityList } from 'components/search/FacilityList';
import { SearchContent } from 'components/search/SearchContent';
import { VehicleLengthTool } from 'components/search/VehicleLengthTool';
import { SearchBreadcrumbs } from 'components/search/SearchBreadcrumbs';
import { SearchBreadcrumbsV2 } from 'components/search/SearchBreadcrumbsV2';
import { MarkdownWrapper } from 'components/search/MarkdownWrapper';
import ReviewCardList from 'components/review/ReviewCard/ReviewCardList';
import { StickySearchBar } from 'components/landing/StickySearchBar';
import GiftCardBanner from 'components/Banners/GiftCardBanner/GiftCardBanner';
import GiftCardModalLink from 'components/giftcard/GiftCardModalLink/GiftCardModalLink';

import './SearchResults.scss';
import AverageUnitPriceHeader from '../AverageUnitPrice/AverageUnitPriceHeader';
import AverageUnitPriceChart from '../AverageUnitPrice/AverageUnitPriceChart';
import { BestPricedUnits } from '../SearchContent/BestPricedUnits';
import { formatMonthDayYear } from '../AverageUnitPrice/utils';

export default function SearchResults({
	facilities,
	facilitiesPerPage,
	loading,
	primaryHeadline,
	secondaryHeadline,
	moveInDate,
	openInteractiveMap,
	openHoursRequest,
	phone,
	localPhone,
	queryParams,
	searchLocation,
	searchContent,
	searchParams,
	searchTerm,
	searchType,
	searchId,
	searchPath,
	openFilters,
	totalFacilities,
	page,
	locationLabel,
	handleSearchParamChange,
	numPages,
	injectedComponents,
	injectAllEvenIfNoContent,
	listType,
	canonicalUrl,
	cityUrl,
	stateUrl,
	showVehicleLengthTool,
	handleSearch,
	getLocalPhoneNumberByZipCode,
	getLocalPhoneNumberByCityState,
	getTollfreePhoneNumber,
	faqContent,
	faqShowing,
	firstListingIsVipEligible,
	vipAccount,
	vipFacilityId,
	vipFacilityImages,
	vipShowMapHandler,
	isStorageDealsPage,
	fmfFacilitiesCount,
	promoFacilitiesCount,
	isCLP,
	baseUrl,
	bestPricedUnits,
	cityDrilldown,
}) {
	const isInformedSearch = searchParams.informedSearch;
	const showSearchHero =
		searchParams.subPageType !== 'cityLandingPage' &&
		!isInformedSearch &&
		!isStorageDealsPage;
	const showFirstMonthFreePill = isStorageDealsPage && !isInformedSearch;
	const { seoBodyJson } = searchContent;

	const hasTpReviews = !!searchContent?.tpReviews?.length;

	const averageUnitPriceData = searchContent?.averageMonthlyUnitPrice;

	const hasAveragePriceHistory = !!averageUnitPriceData?.priceHistory?.length;

	const refreshDate =
		hasAveragePriceHistory &&
		averageUnitPriceData?.priceHistory[
			averageUnitPriceData?.priceHistory?.length - 1
		]?.date;

	return (
		<div
			className={classNames('search-results', { loading })}
			id="search-results"
		>
			{/* INFORMED SEARCH HEADER */}
			{/* This is the main title of the to of the Informed Search results listing.
				It includes the "How it works"  steps.
			*/}
			{isInformedSearch && (
				<Group className="informed-search-hero">
					<H1
						size="large"
						className="main-header"
					>
						Self-storage facilities centered around{' '}
						{searchLocation.city}, {searchLocation.state}{' '}
						{searchLocation.zip ? ` (${searchLocation.zip})` : ''}
					</H1>
					<HowItWorks />
				</Group>
			)}
			{/* END INFORMED SEARCH HEADER */}

			{showSearchHero && (
				<SearchHero
					location={searchTerm}
					subheading={secondaryHeadline}
					headline={primaryHeadline}
					standardHeadlines
					moveInDate={moveInDate}
					searchParams={searchParams}
					searchType={searchType}
					isSearchResultsPage
				/>
			)}

			{showSearchHero && (
				<StickySearchBar
					triggerId="hero-sticky-search"
					handleSearch={handleSearch}
				/>
			)}
			{/* VEHICLE LENGTH TOOL */}
			{showVehicleLengthTool && <VehicleLengthTool />}

			{/* MOBILE FILTERS */}
			{isInformedSearch && (
				<div>
					<Section className="mobile-search-filters">
						<FiltersButton onClick={openFilters}>
							Filters
						</FiltersButton>
						{/* Pill that appears beside the fitlers button
						on uninformed search deals pages */}
						{showFirstMonthFreePill && (
							<Link
								href={`${cityUrl}#search-results`}
								hideBorder
							>
								<Pill className="fmf-pill-mobile">
									<Text eyebrow>FIRST MONTH FREE ✕</Text>
								</Pill>
							</Link>
						)}
					</Section>
				</div>
			)}

			{/* FILTER BAR */}
			<div>
				<div className="search-controls">
					<div className="pagination-wrapper">
						<PaginationSummary
							hideIfEmpty
							page={page}
							itemsPerPage={facilitiesPerPage}
							totalItems={totalFacilities}
							prefix={searchLocation.text}
							label={`Storage Facilities`}
							isDot={true}
						/>
					</div>
					<div className="controls-wrapper">
						<SortBySelectBox
							onChange={handleSearchParamChange}
							value={queryParams?.order || searchParams?.order}
						/>
					</div>
					{!isInformedSearch && (
						<div className="mobile-filter-button-wrapper">
							<FiltersButton onClick={openFilters}>
								Filters
							</FiltersButton>
						</div>
					)}
				</div>
			</div>

			{/* MOBILE MAP */}
			{/* This map only appears on mobile. It's an alternative to the map
			that appears in the sidebar, which isn't visible on mobile. */}
			<StaticMap className="mobile-static-map">
				<Button
					className="view-map-button"
					onClick={openInteractiveMap}
				>
					View map
				</Button>
			</StaticMap>
			<GiftCardBanner
				className="gift-card-modal-link-container"
				content={<GiftCardModalLink showTopText />}
			/>

			<div className={classNames('serp-container')}>
				<FacilityList
					facilities={facilities}
					openModal={openHoursRequest}
					phone={localPhone}
					showNoResults={!loading}
					searchType={searchType}
					page={page}
					facilitiesPerPage={facilitiesPerPage}
					injectedComponents={injectedComponents}
					injectAllEvenIfNoContent={injectAllEvenIfNoContent}
					listType={listType}
					narrow={listType === 'grid'}
					getLocalPhoneNumberByZipCode={getLocalPhoneNumberByZipCode}
					getLocalPhoneNumberByCityState={
						getLocalPhoneNumberByCityState
					}
					getTollfreePhoneNumber={getTollfreePhoneNumber}
					searchId={searchId}
					fmfFacilitiesCount={fmfFacilitiesCount}
					promoFacilitiesCount={promoFacilitiesCount}
					location={searchLocation}
					firstListingIsVipEligible={firstListingIsVipEligible}
					vipAccount={vipAccount}
					vipFacilityId={vipFacilityId}
					vipFacilityImages={vipFacilityImages}
					vipShowMapHandler={vipShowMapHandler}
					isStorageDealsPage={isStorageDealsPage}
					isCLP={isCLP}
					baseUrl={baseUrl}
					cityDrilldown={cityDrilldown}
				/>
			</div>

			{numPages > 1 && (
				<Section>
					<SearchPagination
						searchParams={searchParams}
						numPages={numPages}
						selectedPage={page}
						searchPath={
							isStorageDealsPage
								? STORAGE_DEALS_SEARCH_PATH
								: searchPath
						}
					/>
				</Section>
			)}
			{phone && (
				<Section>
					<Container>
						<CallCenterCTA
							phone={phone}
							getTollfreePhoneNumber={getTollfreePhoneNumber}
						/>
					</Container>
				</Section>
			)}

			{searchParams.subPageType === 'cityLandingPage' && (
				<Section>
					<Container>
						<SearchBox
							inputLabel="Enter ZIP code"
							handleSearch={handleSearch}
						/>
					</Container>
				</Section>
			)}

			<div className="search-content-container">
				{hasTpReviews && (
					<Section className="tp-reviews-section both-borders">
						<ReviewCardList reviews={searchContent.tpReviews} />
					</Section>
				)}
				<SearchContent
					location={searchLocation}
					content={searchContent}
					searchParams={searchParams}
				/>
				{seoBodyJson && (
					<Section className="markdown-wrapper">
						<MarkdownWrapper content={seoBodyJson} />
					</Section>
				)}
				<SearchContent
					faqShowing={faqShowing}
					faqContent={faqContent}
				/>
				{hasAveragePriceHistory && (
					<Section className="average-price-data-section top-border">
						<Group>
							{isCLP ? (
								<>
									<H2
										id="pricing"
										className="sf-type-large"
										weight="bold"
									>
										How much do storage units cost in{' '}
										{searchLocation.city}?
									</H2>

									<Paragraph>
										The biggest factor when it comes to the
										cost of storage units in
										{searchLocation.city}
										is unit size. Larger storage units. like
										10x10 and bigger, will generally cost
										more than smaller storage units, which
										range from 5x5 to 5x10 in size. Other
										factors that can increase self-storage
										prices include the storage unit
										amenities and special features.
										Self-storage facilities in{' '}
										{searchLocation.city} that offer
										additional amenities like indoor climate
										control, 24-hour storage access, extra
										security features, and added
										conveniences like first floor storage
										options and elevators tend to be more
										expensive than storage facilities that
										offer basic services.
									</Paragraph>

									<Paragraph>
										No matter what your storage needs are,
										you can easily find a cheap storage unit
										in {searchLocation.city}. Compare the
										available facilities on SpareFoot and
										filter by preferred storage amenities
										and features.
									</Paragraph>
								</>
							) : (
								<>
									<H2
										id="pricing"
										className="sf-type-large"
										weight="bold"
									>
										Pricing for{' '}
										{searchContent.formattedStorageType} in{' '}
										{searchLocation.city}?
									</H2>

									<Paragraph>
										The biggest factor when it comes to{' '}
										{searchContent.formattedStorageType}{' '}
										prices in {searchLocation.city} is size.
										Below are some monthly price ranges you
										can expect to see in{' '}
										{searchLocation.city} based on different
										storage unit sizes.
									</Paragraph>
								</>
							)}
						</Group>

						<AverageUnitPriceHeader
							averagePriceData={averageUnitPriceData}
							city={searchLocation?.city}
						/>
						<AverageUnitPriceChart
							averagePriceData={averageUnitPriceData}
						/>

						{!!bestPricedUnits?.length && (
							<div className="best-priced-units-container">
								<BestPricedUnits
									location={{
										city: searchLocation?.city,
										state: searchLocation?.state,
									}}
									units={bestPricedUnits}
									refreshDate={
										!!refreshDate &&
										formatMonthDayYear(refreshDate)
									}
									vehicleType={searchParams?.vehicle}
								/>
							</div>
						)}
					</Section>
				)}
			</div>
			<Section>
				{searchParams.subPageType === 'cityLandingPage' ? (
					<SearchBreadcrumbsV2
						location={searchLocation}
						canonicalUrl={canonicalUrl}
						cityUrl={cityUrl}
						stateUrl={stateUrl}
						searchParams={searchParams}
					/>
				) : (
					<SearchBreadcrumbs
						location={searchLocation}
						canonicalUrl={canonicalUrl}
						cityUrl={cityUrl}
						stateUrl={stateUrl}
						searchParams={searchParams}
					/>
				)}
			</Section>
		</div>
	);
}

SearchResults.propTypes = {
	facilities: PropTypes.arrayOf(PropTypes.object),
	facilitiesPerPage: PropTypes.number,
	handleSearchParamChange: PropTypes.func,
	loading: PropTypes.bool,
	locationLabel: PropTypes.string,
	queryParams: PropTypes.object,
	searchContent: PropTypes.object,
	searchTerm: PropTypes.string,
	primaryHeadline: PropTypes.string.isRequired,
	secondaryHeadline: PropTypes.string.isRequired,
	numPages: PropTypes.number,
	page: PropTypes.number.isRequired,
	searchLocation: PropTypes.object,
	searchParams: PropTypes.object,
	searchType: PropTypes.string,
	searchId: PropTypes.string,
	searchPath: PropTypes.string,
	phone: PropTypes.object,
	localPhone: PropTypes.object,
	totalFacilities: PropTypes.number,
	openFilters: PropTypes.func.isRequired,
	moveInDate: PropTypes.instanceOf(Date),
	openInteractiveMap: PropTypes.func,
	openHoursRequest: PropTypes.func,
	injectedComponents: PropTypes.object,
	injectAllEvenIfNoContent: PropTypes.bool,
	listType: PropTypes.oneOf(['grid', 'column']),
	canonicalUrl: PropTypes.string,
	cityUrl: PropTypes.string,
	stateUrl: PropTypes.string,
	/** Vehicle Length Tool Toggle - Deprecated until API is upgraded */
	showVehicleLengthTool: PropTypes.bool,
	handleSearch: PropTypes.func.isRequired,
	getLocalPhoneNumberByZipCode: PropTypes.func.isRequired,
	getLocalPhoneNumberByCityState: PropTypes.func.isRequired,
	getTollfreePhoneNumber: PropTypes.func.isRequired,
	faqContent: PropTypes.arrayOf(PropTypes.object),
	faqShowing: PropTypes.bool,
	firstListingIsVipEligible: PropTypes.bool,
	vipAccount: PropTypes.oneOfType([PropTypes.object, PropTypes.bool]),
	vipFacilityId: PropTypes.number,
	vipFacilityImages: PropTypes.arrayOf(
		PropTypes.shape({
			src: PropTypes.string,
			atl: PropTypes.string,
		})
	),
	vipShowMapHandler: PropTypes.func,
	isStorageDealsPage: PropTypes.bool,
	fmfFacilitiesCount: PropTypes.number,
	promoFacilitiesCount: PropTypes.number,
	isCLP: PropTypes.bool,
	baseUrl: PropTypes.string,
	bestPricedUnits: PropTypes.arrayOf(
		PropTypes.shape({
			averagePrice: PropTypes.number,
			facility: PropTypes.number,
			facilityCity: PropTypes.string,
			facilityImage: PropTypes.string,
			facilityName: PropTypes.string,
			facilityState: PropTypes.string,
			inNetwork: PropTypes.bool,
			price: PropTypes.string,
			reserveUrl: PropTypes.string,
			sizeText: PropTypes.string,
			unit: PropTypes.number,
			squareFootage: PropTypes.number,
		})
	),
	cityDrilldown: PropTypes.arrayOf(PropTypes.object),
};
